import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  BatteryCharging,
  Cpu,
  ShieldCheck,
  Smartphone,
  Wrench,
  Zap,
} from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, SectionLabel } from "@/components/Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1746005718004-1f992c399428?crop=entropy&cs=srgb&fm=jpg&q=85";
const TOOLS_IMG =
  "https://images.unsplash.com/photo-1611396000732-f8c9a933424f?crop=entropy&cs=srgb&fm=jpg&q=85";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const chapters = [
  {
    n: "01",
    icon: Smartphone,
    title: "Display & Glas",
    text: "Rissiges Display, gesprungenes Glas oder defekter Touchscreen – wir tauschen dein Display schnell und in hochwertiger Qualität.",
  },
  {
    n: "02",
    icon: BatteryCharging,
    title: "Akku & Power",
    text: "Dein Akku hält nicht mehr durch? Wir wechseln ihn aus – für neue Energie und volle Leistung über den ganzen Tag.",
  },
  {
    n: "03",
    icon: Cpu,
    title: "Software & Daten",
    text: "Von Software-Problemen bis zur Einrichtung: Wir bringen dein Gerät wieder zum Laufen – verlässlich und sicher.",
  },
  {
    n: "04",
    icon: Zap,
    title: "Express-Service",
    text: "Viele Reparaturen erledigen wir innerhalb von nur 1 Stunde – ehrlich, transparent und ohne langes Warten.",
  },
];

const brandRepairs = {
  iPhone: ["Display-Tausch", "Akku-Wechsel", "Software-Problem", "Sonstiges"],
  Samsung: ["Display-Tausch", "Akku-Wechsel", "Software-Problem", "Sonstiges"],
  Xiaomi: ["Display-Tausch", "Akku-Wechsel", "Software-Problem", "Sonstiges"],
  iPad: ["Display & Glas", "Akku-Wechsel", "Software-Problem", "Sonstiges"],
};

const marqueeItems = [
  "1-Stunde Express-Service",
  "90 Tage Geld-zurück-Garantie",
  "Original- & Premium-Ersatzteile",
  "Smartphone & Tablet",
  "Kostenloser Kostenvoranschlag",
];

const Home = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const imgY = useTransform(scrollY, [0, 700], [0, 110]);
  const [brand, setBrand] = useState("iPhone");
  const [repair, setRepair] = useState("Display-Tausch");

  const startInquiry = () => {
    navigate(
      `/kontakt?geraet=${encodeURIComponent(brand)}&anliegen=${encodeURIComponent(repair)}`
    );
  };

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center bg-blueprint overflow-hidden">
        <div
          className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(255,230,0,0.13) 0%, transparent 65%)" }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-32 pb-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-14 items-center">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-mono2 text-[11px] sm:text-xs tracking-[0.3em] uppercase text-[#FFE600] flex items-center gap-3"
            >
              <span className="inline-block w-8 h-px bg-[#FFE600]" />
              Handy-Reparatur · Odisheim
            </motion.p>

            <h1 className="mt-6 font-display font-extrabold uppercase leading-[0.88] tracking-tight text-[19vw] sm:text-8xl lg:text-[7.5rem]">
              <MaskedLine delay={0.15}>
                <span className="text-white">Handy</span>
              </MaskedLine>
              <MaskedLine delay={0.28}>
                <span className="text-outline-white">kaputt?</span>
              </MaskedLine>
              <MaskedLine delay={0.41}>
                <span className="text-[#FFE600] italic">Hero hilft.</span>
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65 }}
              className="mt-7 text-gray-300 text-base sm:text-lg leading-relaxed max-w-xl"
            >
              Schnelle und zuverlässige Lösungen für Smartphones und Tablets.
              HeroPhone bietet umfassende Reparaturservices für deine mobilen
              Geräte – von Displays über Batterien bis hin zu Softwareproblemen.
              Vertraue auf unsere Erfahrung!
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8 }}
              className="mt-6 flex flex-wrap gap-3"
            >
              <span className="flex items-center gap-2.5 border border-[#FFE600]/40 bg-[#FFE600]/10 text-[#FFE600] font-mono2 text-[11px] tracking-[0.15em] uppercase px-4 py-2 rounded-full">
                <span className="w-2 h-2 rounded-full bg-[#FFE600] animate-pulse-dot" />
                1h Express-Service
              </span>
              <span className="flex items-center gap-2.5 border border-white/15 text-gray-300 font-mono2 text-[11px] tracking-[0.15em] uppercase px-4 py-2 rounded-full">
                <ShieldCheck className="w-3.5 h-3.5 text-[#FFE600]" />
                90 Tage Geld-zurück-Garantie
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.95 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/kontakt"
                data-testid="hero-cta-button"
                className="group bg-[#FFE600] text-[#090A0C] font-display font-bold uppercase tracking-wide text-lg px-8 py-4 rounded-sm flex items-center gap-3 hover:bg-[#FFF033] hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
              >
                Jetzt Reparatur anfragen
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
              <Link
                to="/leistungen"
                data-testid="hero-services-link"
                className="font-display font-semibold uppercase tracking-wide text-lg text-white border-b-2 border-[#FFE600]/60 pb-1 hover:text-[#FFE600] hover:border-[#FFE600] transition-colors duration-300"
              >
                Leistungen ansehen
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1.15 }}
              className="mt-12 grid grid-cols-3 max-w-md divide-x divide-white/10 border-y border-white/10"
            >
              {[
                ["≈ 1 Std.", "Express-Reparatur"],
                ["90 Tage", "Garantie"],
                ["100 %", "ehrlicher Service"],
              ].map(([big, small]) => (
                <div key={small} className="py-4 px-4 first:pl-0">
                  <p className="font-display text-2xl sm:text-3xl font-bold text-[#FFE600]">{big}</p>
                  <p className="text-[11px] sm:text-xs text-gray-500 uppercase tracking-wider mt-1">{small}</p>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative hidden lg:block"
          >
            <motion.div style={{ y: imgY }} className="relative">
              <div className="absolute -inset-3 border border-[#FFE600]/50 rounded-md translate-x-5 translate-y-5" />
              <div className="relative rounded-md overflow-hidden border border-white/15">
                <img
                  src={HERO_IMG}
                  alt="Zerlegtes Smartphone bei der Reparatur"
                  className="w-full h-[540px] object-cover"
                  data-testid="hero-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#090A0C]/85 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between bg-[#090A0C]/80 backdrop-blur-md border border-white/10 rounded-sm px-5 py-4">
                  <div>
                    <p className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-gray-400">Status</p>
                    <p className="font-display text-xl font-semibold uppercase text-white">Werkstatt geöffnet</p>
                  </div>
                  <Wrench className="w-6 h-6 text-[#FFE600]" />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="relative py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Was wir reparieren</SectionLabel>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase text-white leading-[0.92] max-w-3xl">
              Professionelle Reparaturen. <span className="text-[#FFE600]">Kapitel für Kapitel.</span>
            </h2>
          </Reveal>

          <div className="mt-14 grid sm:grid-cols-2 gap-5">
            {chapters.map((ch, i) => (
              <Reveal key={ch.n} delay={i * 0.08}>
                <div
                  data-testid={`chapter-card-${ch.n}`}
                  className="glow-card group relative bg-[#111318] rounded-md p-8 sm:p-10 overflow-hidden h-full"
                >
                  <span className="absolute -top-6 right-4 font-display font-extrabold text-[7rem] leading-none text-white/[0.05] group-hover:text-[#FFE600]/10 transition-colors duration-500 select-none">
                    {ch.n}
                  </span>
                  <ch.icon className="w-8 h-8 text-[#FFE600]" strokeWidth={1.75} />
                  <p className="mt-6 font-mono2 text-[11px] tracking-[0.25em] uppercase text-gray-500">
                    Kapitel {ch.n}
                  </p>
                  <h3 className="mt-2 font-display text-3xl sm:text-4xl font-bold uppercase text-white">
                    {ch.title}
                  </h3>
                  <p className="mt-4 text-gray-400 text-sm sm:text-base leading-relaxed">{ch.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee items={marqueeItems} />

      {/* QUICK SELECTOR */}
      <section className="py-24 sm:py-32 bg-[#0B0D11]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <SectionLabel>Anfrage in 10 Sekunden</SectionLabel>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl font-extrabold uppercase text-white leading-[0.92]">
              Wähle Gerät. <br />
              <span className="text-[#FFE600]">Wir kümmern uns.</span>
            </h2>
            <p className="mt-6 text-gray-400 leading-relaxed max-w-md">
              Sag uns kurz, was kaputt ist – wir melden uns mit einem ehrlichen,
              kostenlosen Kostenvoranschlag. Je genauer die Nachricht, umso
              schneller können wir helfen.
            </p>
            <div className="mt-8 flex items-center gap-4 text-sm text-gray-500">
              <ShieldCheck className="w-5 h-5 text-[#FFE600]" />
              Kein Verkauf. Keine versteckten Kosten. Nur Klartext.
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="glow-card bg-[#111318] rounded-md p-7 sm:p-9">
              <p className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-gray-500 mb-4">
                01 · Dein Gerät
              </p>
              <div className="flex flex-wrap gap-2.5">
                {Object.keys(brandRepairs).map((b) => (
                  <button
                    key={b}
                    data-testid={`selector-brand-${b.toLowerCase()}`}
                    onClick={() => {
                      setBrand(b);
                      setRepair(brandRepairs[b][0]);
                    }}
                    className={`font-display font-semibold uppercase tracking-wide text-lg px-5 py-2.5 rounded-sm border transition-all duration-300 ${
                      brand === b
                        ? "bg-[#FFE600] text-[#090A0C] border-[#FFE600]"
                        : "text-gray-300 border-white/15 hover:border-[#FFE600]/60 hover:text-white"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>

              <p className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-gray-500 mt-8 mb-4">
                02 · Dein Problem
              </p>
              <div className="flex flex-wrap gap-2.5">
                {brandRepairs[brand].map((r) => (
                  <button
                    key={r}
                    data-testid={`selector-repair-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setRepair(r)}
                    className={`text-sm px-4 py-2.5 rounded-sm border transition-all duration-300 ${
                      repair === r
                        ? "bg-[#FFE600]/15 text-[#FFE600] border-[#FFE600]"
                        : "text-gray-400 border-white/15 hover:border-[#FFE600]/60 hover:text-white"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>

              <button
                data-testid="selector-submit-button"
                onClick={startInquiry}
                className="group mt-9 w-full bg-[#FFE600] text-[#090A0C] font-display font-bold uppercase tracking-wide text-lg px-6 py-4 rounded-sm flex items-center justify-center gap-3 hover:bg-[#FFF033] hover:scale-[1.01] active:scale-[0.99] transition-transform duration-200"
              >
                Anfrage für {brand} senden
                <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
              </button>
              <p className="mt-4 text-center text-xs text-gray-500">
                Du wirst zum Kontaktformular weitergeleitet – Felder sind schon vorausgefüllt.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="absolute -inset-3 border border-[#FFE600]/40 rounded-md -translate-x-4 translate-y-4" />
            <div className="relative rounded-md overflow-hidden border border-white/15">
              <img
                src={TOOLS_IMG}
                alt="Reparatur-Werkzeug unter dem Mikroskop"
                className="w-full h-[420px] sm:h-[500px] object-cover"
                data-testid="about-teaser-image"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#090A0C]/70 via-transparent to-[#FFE600]/10" />
            </div>
          </Reveal>
          <Reveal delay={0.15} className="order-1 lg:order-2">
            <SectionLabel>Über HeroPhone</SectionLabel>
            <h2 className="mt-5 font-display text-5xl sm:text-6xl font-extrabold uppercase text-white leading-[0.92]">
              Ehrlicher Service <span className="text-[#FFE600]">ohne Schnickschnack.</span>
            </h2>
            <p className="mt-6 text-gray-300 leading-relaxed">
              HeroPhone ist ein moderner Handy-Reparatur Dienst, der sich auf die
              Reparatur und den Service rund um Smartphones und Tablets
              spezialisiert hat. Unser Ziel ist es, unseren Kunden schnelle,
              zuverlässige und preiswerte Lösungen für ihre mobilen Geräte zu bieten.
            </p>
            <p className="mt-4 text-gray-400 leading-relaxed">
              Bei HeroPhone stehen Kundenzufriedenheit, Qualität und ehrlicher
              Service an erster Stelle.
            </p>
            <Link
              to="/ueber-uns"
              data-testid="about-teaser-link"
              className="group mt-8 inline-flex items-center gap-3 font-display font-semibold uppercase tracking-wide text-lg text-[#FFE600]"
            >
              Mehr über uns
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="relative py-24 sm:py-28 bg-[#FFE600] overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(-45deg, #090A0C 0 2px, transparent 2px 26px)",
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal>
            <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#090A0C]/70">
              Jetzt durchstarten
            </p>
            <h2 className="mt-4 font-display text-5xl sm:text-7xl font-extrabold uppercase text-[#090A0C] leading-[0.9] max-w-4xl">
              Bereit, wenn dein Handy es nicht ist.
            </h2>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                data-testid="cta-banner-form-button"
                className="bg-[#090A0C] text-[#FFE600] font-display font-bold uppercase tracking-wide text-lg px-8 py-4 rounded-sm hover:scale-[1.03] active:scale-[0.98] transition-transform duration-200"
              >
                Reparatur anfragen
              </Link>
              <a
                href="tel:+4915116012024"
                data-testid="cta-banner-call-button"
                className="border-2 border-[#090A0C] text-[#090A0C] font-display font-bold uppercase tracking-wide text-lg px-8 py-4 rounded-sm hover:bg-[#090A0C] hover:text-[#FFE600] transition-colors duration-300"
              >
                0151 16012024
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Home;
